// src/modules/shared/dashboard/forecast.ts
// Simple least-squares linear regression forecast for trend charts.

export type Point = { month: string; total: number };

/**
 * Project `forward` months ahead using linear regression on the last `n`
 * points. Returns the original points unchanged followed by projected points
 * (with `projected: true`).
 *
 * Falls back to returning the original array unchanged when there are fewer
 * than 3 data points (regression is meaningless on tiny samples).
 */
export function linearForecast(
  points: Point[],
  forward: number = 3,
): Array<Point & { projected?: boolean }> {
  if (points.length < 3) return points;

  const n = points.length;
  const xs = points.map((_, i) => i);
  const ys = points.map((p) => p.total);

  const sumX = xs.reduce((a, b) => a + b, 0);
  const sumY = ys.reduce((a, b) => a + b, 0);
  const sumXY = xs.reduce((s, x, i) => s + x * ys[i], 0);
  const sumXX = xs.reduce((s, x) => s + x * x, 0);

  const denom = n * sumXX - sumX * sumX;
  const slope = denom === 0 ? 0 : (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;

  // Parse the last "YYYY-MM" label and increment month by month.
  const last = points[points.length - 1].month;
  const [yearStr, monStr] = last.split("-");
  let year = Number(yearStr);
  let mon = Number(monStr);

  const projected: Array<Point & { projected: true }> = [];
  for (let i = 1; i <= forward; i++) {
    mon += 1;
    if (mon > 12) { mon = 1; year += 1; }
    const x = n - 1 + i;
    projected.push({
      month: `${year}-${String(mon).padStart(2, "0")}`,
      total: Math.max(0, Math.round(slope * x + intercept)),
      projected: true,
    });
  }

  return [...points, ...projected];
}
